import { getCrmAuthToken } from "../../crmAuthToken";

export function authHeaders() {
  const token = getCrmAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Codifica el id para rutas (soporta ids virtuales tipo v:...::...). */
function attachmentPathId(attachmentId) {
  return encodeURIComponent(String(attachmentId ?? ""));
}

/** URL directa al API con JWT en query (iframes no envían Authorization). */
export function buildAttachmentPreviewUrl(attachmentId) {
  const base = `${process.env.REACT_APP_API_URL}/emails/attachment/${attachmentPathId(attachmentId)}/preview`;
  const token = getCrmAuthToken();
  if (!token) return base;
  return `${base}?token=${encodeURIComponent(token)}`;
}

export function isUniboxEmbedded() {
  try {
    return (
      new URLSearchParams(window.location.search).get("embedded") === "1" ||
      window.parent !== window
    );
  } catch {
    return false;
  }
}

/** Abre el PDF en pestaña nueva vía API (evita blob: bloqueado por Chrome/extensiones). */
export function openAttachmentInNewTab(attachmentId) {
  const url = buildAttachmentPreviewUrl(attachmentId);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened) return;

  if (isUniboxEmbedded() && window.parent !== window) {
    window.parent.postMessage({ type: "UNIBOX_OPEN_LINK", url }, "*");
    return;
  }

  throw new Error(
    "El navegador bloqueó la ventana emergente. Permite pop-ups o usa Descargar."
  );
}

export function isImageFile(filename, mimetype) {
  const mime = String(mimetype || "").toLowerCase();
  const name = String(filename || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/.test(name);
}

export async function loadPdfJs() {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;
  return pdfjs;
}

/** Primera página del PDF como JPEG en data URL (miniaturas). */
export async function pdfFirstPageThumbnailDataUrl(arrayBuffer, maxCssPx = 96) {
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const target = Math.max(64, maxCssPx) * 2;
  const scale = target / Math.max(base.width, base.height, 1);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL("image/jpeg", 0.9);
}

export async function fetchAttachmentPreviewBuffer(attachmentId) {
  const id = attachmentPathId(attachmentId);
  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/emails/attachment/${id}/preview`,
    { headers: authHeaders() }
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.arrayBuffer();
}

export function isPdfFile(filename, mimetype) {
  const mime = String(mimetype || "").toLowerCase();
  const name = String(filename || "").toLowerCase();
  return mime.includes("pdf") || name.endsWith(".pdf");
}

export function canInlinePreview(filename, mimetype) {
  return isImageFile(filename, mimetype) || isPdfFile(filename, mimetype);
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function resolveClientBlobType(filename, mimetype, responseType) {
  const fromResponse = String(responseType || "").trim();
  if (fromResponse && fromResponse !== "application/octet-stream") {
    return fromResponse;
  }
  if (isPdfFile(filename, mimetype)) return "application/pdf";
  if (isImageFile(filename, mimetype)) {
    const mime = String(mimetype || "").toLowerCase();
    if (mime.startsWith("image/")) return mime;
    const name = String(filename || "").toLowerCase();
    if (name.endsWith(".png")) return "image/png";
    if (name.endsWith(".gif")) return "image/gif";
    if (name.endsWith(".webp")) return "image/webp";
    return "image/jpeg";
  }
  return fromResponse || "application/octet-stream";
}

export async function fetchAttachmentBlob(attachmentId, filename, mimetype) {
  const id = attachmentPathId(attachmentId);
  const previewRes = await fetch(
    `${process.env.REACT_APP_API_URL}/emails/attachment/${id}/preview`,
    { headers: authHeaders() }
  );

  if (previewRes.ok) {
    const raw = await previewRes.blob();
    const type = resolveClientBlobType(
      filename,
      mimetype,
      previewRes.headers.get("content-type")
    );
    return raw.type && raw.type !== "application/octet-stream"
      ? raw
      : new Blob([raw], { type });
  }

  const downloadRes = await fetch(
    `${process.env.REACT_APP_API_URL}/emails/attachment/${id}/download`,
    { headers: authHeaders() }
  );
  if (!downloadRes.ok) {
    let detail = "";
    try {
      const data = await downloadRes.json();
      detail = data?.error || "";
    } catch {
      detail = await downloadRes.text().catch(() => "");
    }
    throw new Error(detail || `HTTP ${downloadRes.status}`);
  }

  const raw = await downloadRes.blob();
  const type = resolveClientBlobType(
    filename,
    mimetype,
    downloadRes.headers.get("content-type")
  );
  return raw.type && raw.type !== "application/octet-stream"
    ? raw
    : new Blob([raw], { type });
}

export async function downloadAttachmentFile(attachmentId, filename) {
  const id = attachmentPathId(attachmentId);
  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/emails/attachment/${id}/download`,
    { headers: authHeaders() }
  );
  if (!res.ok) {
    let detail = "";
    try {
      const data = await res.json();
      detail = data?.error || "";
    } catch {
      detail = await res.text().catch(() => "");
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }

  const raw = await res.blob();
  const type = resolveClientBlobType(
    filename,
    filename,
    res.headers.get("content-type")
  );
  const blob =
    raw.type && raw.type !== "application/octet-stream"
      ? raw
      : new Blob([raw], { type });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "adjunto";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
